import spartaEventsUtility from "utilities/global/sparta-events-utility/2.3.3/js/sparta-events-utility";
import spartaMediaQueryUtility from "utilities/global/sparta-mediaquery-utility/2.0.4/js/sparta-mediaquery-utility";
import spartaStickyUtility from "utilities/global/sparta-sticky-utility/4.0.4/js/sparta-sticky-utility";
import spartaStoreUtility from "utilities/global/sparta-store-utility/3.0.4/js/sparta-store-utility";
import spartaStyleUtility from "utilities/global/sparta-style-utility/3.2.3/js/sparta-style-utility";
import handlebarsRuntime from "utilities/vendor/handlebars-runtime/4.7.6/js/handlebars.runtime";
import hb from "utilities/vendor/handlebars-runtime/4.7.6/js/handlebars.runtime";
import Module from "utilities/global/sparta-module-utility/6.2.6/js/sparta-module-utility";
import helpersComparisons from "utilities/helpers/helpers-comparisons/1.0.1/js/helpers-comparisons";
import helperComparisons from "utilities/helpers/helpers-comparisons/1.0.1/js/helpers-comparisons";
import TaglineHelper from "utilities/helpers/helpers-tagline/1.0.0/js/tagline-helper";
import spartaTemplates from "modules/global/global-nav-module/10.0.5/js/templates";
import "css!modules/global/global-nav-module/10.0.5/css/global-nav-module.scss.css";
import "css!modules/global/global-nav-module/10.0.5/css/search-stub-overwrite.scss.css";
import "css!modules/global/global-nav-module/10.0.5/css/menu-nav.scss.css";
/* global spartaEventsUtility */

const selectors = {
  navigationContainer: '.js-nav-container',
  subNavigationContainer: '.js-sub-nav-container',
  smallSectionTitle: '.js-nav-section-title',
  smallSectionList: '.js-navigation-list-small',
  smallSectionItems: '.js-navigation-item-small',
};

const cssClasses = {
  open: 'navigation__title--open',
  hide: 'hide',
  hideSmall: 'hide-for-small-up',
  hideMediumUp: 'hide-for-medium-up',
  collapsed: 'collapsed',
};

const events = {
  menuButtonClicked: 'GLOBAL_NAV_MODULE_MENU_BUTTON.clicked',
};

/**
 * MenuNav
 * @export
 * @class
 */
class MenuNav {
  /**
   * Initalized from global-nav-module.js.
   * @param {object} moduleElement Module element.
   * @returns {undefined}
   */
  init(moduleElement) {
    const sectionTitle = [...moduleElement.querySelectorAll(selectors.smallSectionTitle)];
    sectionTitle.forEach((title) => {
      title.addEventListener('click', this.sectionTitleClick.bind(this));
    });

    // Let animations end before acting
    moduleElement.addEventListener('transitionend', MenuNav.hideInvisibleContent, false);

    spartaEventsUtility.sub(events.menuButtonClicked, this.toggleNavigation.bind(
      this,
      moduleElement,
    ));
  }

  /**
   * Toggles the hide class for nav container
   * @param {object} moduleElement Module element.
   * @returns {undefined}
   */
  toggleNavigation(moduleElement) {
    const navigationContainer = moduleElement.querySelector(selectors.navigationContainer);
    // On animation begin, set an explicit height of parent container based on its children.
    requestAnimationFrame(() => {
      navigationContainer.classList.remove(cssClasses.hide);
      requestAnimationFrame(() => {
        // Trigger a transition with the `expanded` class, which will affect stickies.
        navigationContainer.addEventListener('transitionend', function navDoneOnce(e) {
          spartaStickyUtility.recalcStickyEls();

          // Remove this event handler.
          e.target.removeEventListener(e.type, navDoneOnce);
        });
        navigationContainer.classList.toggle('expanded');
      });
    });
  }

  /**
   * Click handler for section title
   * @memberof MenuNav
   * @param {object} event - event object from event listener
   * @returns {undefined}
   */
  sectionTitleClick(event) {
    const element = event.currentTarget;
    const section = element.parentElement;
    const sectionList = section.querySelector(selectors.smallSectionList);
    const sectionListItems = [...sectionList.querySelectorAll(selectors.smallSectionItems)];

    element.classList.toggle(cssClasses.open);
    sectionList.classList.remove(cssClasses.hide);

    requestAnimationFrame(() => {
      this.makeAutoHeightExplicitElements({
        targetElement: sectionList,
        referenceElements: sectionListItems,
      });
      sectionList.classList.toggle(cssClasses.collapsed);
    });
    MenuNav.setAriaExpanded({
      referenceElem: sectionList,
      targetElem: element,
      invert: false,
    });
  }

  /* BEGIN UTIL FUNCTIONS */
  /* eslint-disable class-methods-use-this */
  /**
  * Set the CSS height attribute to a fixed value based on the height of another
  * element on the page. Preferably the child elements of said element.
  * This is helpful for animating (via CSS transform) elements
  * that are laid out with `height: auto`.
  * @param  {object} selectors - CSS selectors to operate
  * @param  {object} targetElements - elements to set height for
  * @param  {array} referenceElements - Selectors for element to read height from
  * @returns {void}
  */
  makeAutoHeightExplicitElements({ targetElement, referenceElements }) {
    let contentHeight = 0;

    referenceElements.forEach((element) => {
      if (element) {
        contentHeight += element.getBoundingClientRect().height;
      }
    });

    if (targetElement) {
      targetElement.style.height = `${contentHeight}px`;
    }
  }
  /* eslint-enable class-methods-use-this */

  /**
   * Event callback to make hidden content inaccessible to screen readers and keyboard navication
   * @param  {object} event - `transitionend` event called when sections are collapsed/expanded
   * @returns {void}
   */
  static hideInvisibleContent(event) {
    const elDimensions = event.target.getBoundingClientRect();
    const hiddenEl = elDimensions.height === 0 || elDimensions.width === 0;

    if (hiddenEl) {
      event.target.classList.add(cssClasses.hide);
    }
  }

  /**
  * @param  {object} Elements - Collection of reference and taget `Elements`.
  * Calls `GlobalFooterModuleJS.syncAttrClass` with some properties already set
  *
  * @param  {HTMLElement} Elements.referenceElem - Element to check for 'collapsed' class on
  * @param  {HTMLElement} Elements.targetElem - Element to set the 'aria-expanded' attribute on
  * @returns {void}
  */
  static setAriaExpanded({ referenceElem, targetElem, invert }) {
    MenuNav.syncAttrClass({
      referenceElem,
      targetElem,
      attr: 'aria-expanded',
      className: cssClasses.collapsed,
      invert,
    });
  }

  /**
   * Check if `className` is present on `referenceElem` and set `attr` to `true`
   * if present or `false` if absent
   * @param {object} args - Configuration object
   * @param {HTMLElement} args.referenceElem - Element to check for 'className' class on
   * @param {HTMLElement} args.targetElem - Element to set the attribute on
   * @param {string} args.attr - Attribute to change
   * @param {string} args.className - Name of the class check for
   * @param {boolean} args.invert - `attr` should be false if `className` is present
   *
   * @returns {undefined}
   */
  static syncAttrClass({
    referenceElem,
    targetElem,
    attr,
    className,
    invert,
  }) {
    let attrVal = referenceElem.classList.contains(className);

    if (invert) {
      attrVal = !attrVal;
    }

    targetElem.setAttribute(attr, attrVal);
  }
}

export default new MenuNav();
